import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../auth/services/auth.service';
import {
  AsistenciaRepository,
  EntidadesPorTabla,
  TablaAsistencia,
  TablaConId,
  UnidadTrabajo
} from '../ports/asistencia.repository';
import {
  AsistenciaEntity,
  BANDERAS_POR_DEFECTO,
  BlobEntity,
  CicloEntity,
  crearCicloBase,
  DatosCiclo,
  GrupoEntity,
  HabilitacionEntity,
  InscripcionEntity,
  KvEntity,
  ModuloEntity,
  ParticipanteEntity,
  ROLES_FORMATIVOS_BASE,
  RolFormativoEntity,
  SeguimientoEntity,
  SesionEntity,
  SolicitudEntity,
  UsuarioEntity
} from '../models/asistencia.model';
import {
  AlertaEntity,
  CorreoSalidaEntity,
  PlantillaCorreoEntity,
  PreferenciaNotificacionEntity
} from '../models/alertas.model';
import { EventoAuditoria } from '../models/auditoria.model';

interface EstadoPersistidoAsistencia {
  ciclos: CicloEntity[];
  roles: RolFormativoEntity[];
  modulos: ModuloEntity[];
  grupos: GrupoEntity[];
  sesiones: SesionEntity[];
  usuarios: UsuarioEntity[];
  participantes: ParticipanteEntity[];
  habilitaciones: HabilitacionEntity[];
  inscripciones: InscripcionEntity[];
  asistencias: AsistenciaEntity[];
  solicitudes: SolicitudEntity[];
  seguimientos: SeguimientoEntity[];
  blobs: BlobEntity[];
  alertas: AlertaEntity[];
  correosSalida: CorreoSalidaEntity[];
  preferencias: PreferenciaNotificacionEntity[];
  plantillas: PlantillaCorreoEntity[];
  kv: KvEntity[];
  auditoria: EventoAuditoria[];
}

const STORAGE_KEY_LEGACY_V1 = 'mj_asistencia_mml_estado_v1';
const STORAGE_KEY_LEGACY_V2 = 'mj_asistencia_mml_estado_v2';
const STORAGE_KEY = 'mj_asistencia_mml_estado_v3';
const BROADCAST_CHANNEL = 'mj_asistencia_mml_sync_v3';

function normalizarCiclo(raw: Partial<CicloEntity> | undefined, ahoraIso = new Date().toISOString()): CicloEntity {
  const base = crearCicloBase(ahoraIso);
  if (!raw) return base;
  const rAny = raw as Record<string, unknown>;
  const banderasRaw = (raw.banderas ?? {}) as Partial<typeof BANDERAS_POR_DEFECTO>;
  const minAntes = Number.isFinite(banderasRaw.ventanaAperturaMinAntes)
    ? Number(banderasRaw.ventanaAperturaMinAntes)
    : (Number.isFinite(rAny['ventanaAntesMin']) ? Number(rAny['ventanaAntesMin']) : BANDERAS_POR_DEFECTO.ventanaAperturaMinAntes);
  const horasDespues = Number.isFinite(banderasRaw.ventanaCierreHorasDespues)
    ? Number(banderasRaw.ventanaCierreHorasDespues)
    : (Number.isFinite(rAny['ventanaDespuesMin']) ? Math.max(1, Math.round(Number(rAny['ventanaDespuesMin']) / 60)) : BANDERAS_POR_DEFECTO.ventanaCierreHorasDespues);

  const esFechaIso = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

  return {
    ...base,
    ...raw,
    id: raw.id || base.id,
    codigo: raw.codigo || base.codigo,
    slug: raw.slug || base.slug,
    nombre: raw.nombre || base.nombre,
    oferta: raw.oferta || base.oferta,
    fechaInicio: esFechaIso(raw.fechaInicio) ? raw.fechaInicio : base.fechaInicio,
    fechaFin: esFechaIso(raw.fechaFin) ? raw.fechaFin : base.fechaFin,
    inscripcionAbre: esFechaIso(raw.inscripcionAbre) ? raw.inscripcionAbre : base.inscripcionAbre,
    inscripcionCierra: esFechaIso(raw.inscripcionCierra) ? raw.inscripcionCierra : base.inscripcionCierra,
    permiteCambioGrupoHasta: esFechaIso(raw.permiteCambioGrupoHasta) ? raw.permiteCambioGrupoHasta : base.permiteCambioGrupoHasta,
    zonaHoraria: raw.zonaHoraria || base.zonaHoraria,
    umbrales: {
      ...base.umbrales,
      ...(raw.umbrales ?? {}),
      certificacionMinPct: (raw.id || base.id) === 'ciclo-2026-rc' && raw.umbrales?.certificacionMinPct === 80
        ? 70
        : (raw.umbrales?.certificacionMinPct ?? base.umbrales.certificacionMinPct)
    },
    banderas: {
      ...BANDERAS_POR_DEFECTO,
      ...banderasRaw,
      ventanaAperturaMinAntes: minAntes,
      ventanaCierreHorasDespues: horasDespues
    },
    creadoEn: raw.creadoEn || base.creadoEn,
    actualizadoEn: raw.actualizadoEn || base.actualizadoEn
  };
}

function deduplicarAlertas(alertas: AlertaEntity[]): AlertaEntity[] {
  if (!Array.isArray(alertas) || alertas.length === 0) return [];
  const prioridadEstado = (estado: AlertaEntity['estado']): number => {
    if (estado === 'ATENDIDA') return 4;
    if (estado === 'DESCARTADA') return 3;
    if (estado === 'LEIDA') return 2;
    return 1;
  };

  const limpiarEtiquetaClaseRedundante = (txt: string | undefined): string => {
    if (!txt) return '';
    return txt.replace(/\b(Clase\s+\d+)\s*\(\s*Clase\s+\d+\s*\)/gi, '$1');
  };

  const porClave = new Map<string, AlertaEntity>();
  for (const rawAl of alertas) {
    if (!rawAl) continue;
    const al: AlertaEntity = {
      ...rawAl,
      titulo: limpiarEtiquetaClaseRedundante(rawAl.titulo),
      mensaje: limpiarEtiquetaClaseRedundante(rawAl.mensaje)
    };
    const clave = al.dedupKey || al.id;
    const previa = porClave.get(clave);
    if (!previa) {
      porClave.set(clave, al);
      continue;
    }
    const prioNueva = prioridadEstado(al.estado);
    const prioPrevia = prioridadEstado(previa.estado);
    if (prioNueva > prioPrevia) {
      porClave.set(clave, { ...al, id: previa.id || al.id });
    }
  }

  const unicas = Array.from(porClave.values());
  const sesionesConMen02Abierta = new Set<string>();
  const men05CanonicaPorMentorEstudiante = new Map<string, string>();

  const ordenadasRecientes = [...unicas].sort((a, b) => (b.creadaEn || '').localeCompare(a.creadaEn || ''));
  for (const al of ordenadasRecientes) {
    if (
      al.tipo === 'MEN-02' &&
      !al.dedupKey?.endsWith('|INCOMPLETA') &&
      (al.estado === 'NUEVA' || al.estado === 'LEIDA') &&
      al.contexto?.sesionId
    ) {
      sesionesConMen02Abierta.add(`${al.destinatarioId}|${al.contexto.sesionId}`);
    }
    if (
      al.tipo === 'MEN-05' &&
      (al.estado === 'NUEVA' || al.estado === 'LEIDA') &&
      al.contexto?.participanteId
    ) {
      const kEst = `${al.destinatarioId}|${al.contexto.participanteId}`;
      const existenteId = men05CanonicaPorMentorEstudiante.get(kEst);
      if (!existenteId) {
        men05CanonicaPorMentorEstudiante.set(kEst, al.id);
      } else if (!al.contexto.sesionId) {
        // Preferir la alerta consolidada de seguimiento del estudiante sobre alertas individuales por sesión cuando coexisten abiertas
        men05CanonicaPorMentorEstudiante.set(kEst, al.id);
      }
    }
  }

  return unicas.map(al => {
    if (al.dedupKey?.endsWith('|INCOMPLETA') && (al.estado === 'NUEVA' || al.estado === 'LEIDA')) {
      return { ...al, estado: 'ATENDIDA' as const, motivoCierre: al.motivoCierre || 'UNIFICADA_AUTO' };
    }
    if (
      al.tipo === 'MEN-03' &&
      (al.estado === 'NUEVA' || al.estado === 'LEIDA') &&
      al.contexto?.sesionId &&
      sesionesConMen02Abierta.has(`${al.destinatarioId}|${al.contexto.sesionId}`)
    ) {
      return { ...al, estado: 'ATENDIDA' as const, motivoCierre: al.motivoCierre || 'UNIFICADA_AUTO' };
    }
    if (
      al.tipo === 'MEN-05' &&
      (al.estado === 'NUEVA' || al.estado === 'LEIDA') &&
      al.contexto?.participanteId
    ) {
      const kEst = `${al.destinatarioId}|${al.contexto.participanteId}`;
      const canonicaId = men05CanonicaPorMentorEstudiante.get(kEst);
      if (canonicaId && canonicaId !== al.id) {
        return { ...al, estado: 'ATENDIDA' as const, motivoCierre: al.motivoCierre || 'UNIFICADA_AUTO' };
      }
    }
    return al;
  });
}

function crearEstadoInicialLimpio(): EstadoPersistidoAsistencia {
  const ahora = new Date().toISOString();
  return {
    ciclos: [crearCicloBase(ahora)],
    roles: [...ROLES_FORMATIVOS_BASE],
    modulos: [],
    grupos: [],
    sesiones: [],
    usuarios: [],
    participantes: [],
    habilitaciones: [],
    inscripciones: [],
    asistencias: [],
    solicitudes: [],
    seguimientos: [],
    blobs: [],
    alertas: [],
    correosSalida: [],
    preferencias: [],
    plantillas: [],
    kv: [],
    auditoria: []
  };
}

import { ColaSincronizacionMmlService } from '../../services/cola-sincronizacion-mml.service';
import { HASH_GENESIS, sellarEvento } from '../domain/auditoria';
import { generarId } from '../../services/ejecutor-comandos';
import { AsistenciaSesionService } from '../../services/asistencia-sesion.service';

@Injectable({ providedIn: 'root' })
export class HttpAsistenciaRepository extends AsistenciaRepository {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly colaSync = inject(ColaSincronizacionMmlService);
  private readonly sesionSrv = inject(AsistenciaSesionService);
  private readonly apiUrl = environment.URL_SERVER.endsWith('/') ? environment.URL_SERVER : `${environment.URL_SERVER}/`;

  private readonly estado$ = new BehaviorSubject<EstadoPersistidoAsistencia>(this.cargarDesdeCacheLocal());
  private canalBroadcast?: BroadcastChannel;
  private readonly cargaInicialPromise: Promise<void>;
  private versionEscritura = 0;
  private escriturasEnCurso = 0;
  private lecturaEnCursoPromise: Promise<void> | null = null;
  private releerPendienteForzado = false;
  private ultimaHuellaEstado = '';
  private ultimaEscrituraMs = 0;
  private ultimaLecturaExitosaMs = 0;

  constructor() {
    super();

    this.colaSync.registrarCallbacksRepositorio({
      onEvidenciaConfirmada: async (info) => {
        await this.registrarEvidenciaConfirmadaEnEstado(info);
      },
      onSincronizacionExitosa: () => {
        void this.sincronizarDesdeBackend(true);
      }
    });

    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.canalBroadcast = new BroadcastChannel(BROADCAST_CHANNEL);
      this.canalBroadcast.onmessage = (ev: MessageEvent<{ ts?: number; soloLatido?: boolean }>) => {
        this.ultimaLecturaExitosaMs = Date.now();
        if (ev?.data?.soloLatido) {
          return;
        }
        this.versionEscritura++;
        this.ultimaEscrituraMs = Date.now();
        const recargado = this.cargarDesdeCacheLocal();
        this.ultimaHuellaEstado = this.huellaEstado(recargado);
        this.estado$.next(recargado);
      };
    }
    this.cargaInicialPromise = this.inicializarEstadoYBackend();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (!this.colaSync.tienePendientes()) {
          void this.sincronizarDesdeBackend(true);
        }
      });
      window.addEventListener('focus', () => {
        if (navigator.onLine) {
          void this.sincronizarDesdeBackend();
        }
      });
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible' && navigator.onLine) {
            void this.sincronizarDesdeBackend();
          }
        });
      }
      const intervaloMs = 20000 + Math.floor(Math.random() * 4000);
      setInterval(() => {
        if (
          (typeof document === 'undefined' || document.visibilityState === 'visible') &&
          (typeof navigator === 'undefined' || navigator.onLine)
        ) {
          void this.sincronizarDesdeBackend();
        }
      }, intervaloMs);
    }

    let ultimoUserId: number | string | undefined;
    let primeraEmisionUser = true;
    this.authService.user$.subscribe((u: any) => {
      const nuevoId = u?.id ?? u?.user_id ?? u?.email ?? undefined;
      if (primeraEmisionUser) {
        primeraEmisionUser = false;
        ultimoUserId = nuevoId;
        return;
      }
      if (nuevoId !== ultimoUserId) {
        ultimoUserId = nuevoId;
        if (typeof navigator === 'undefined' || navigator.onLine) {
          void this.sincronizarDesdeBackend(true);
        }
      }
    });
  }

  private async inicializarEstadoYBackend(): Promise<void> {
    await this.colaSync.esperarHidratacionInicial();

    // 1. Si no hay grupos/sesiones en localStorage (o se limpió), intentar recuperar snapshot completo de IndexedDB
    const actual = this.estado$.value;
    if (actual.sesiones.length === 0 && actual.grupos.length === 0) {
      const desdeIdb = await this.colaSync.cargarSnapshotIndexedDb<Partial<EstadoPersistidoAsistencia>>();
      if (desdeIdb && (desdeIdb.sesiones?.length || desdeIdb.grupos?.length)) {
        const restaurado = this.aplicarDeltaPendienteSobreEstado({
          ...crearEstadoInicialLimpio(),
          ...desdeIdb,
          ciclos: desdeIdb.ciclos?.length ? desdeIdb.ciclos.map(c => normalizarCiclo(c)) : actual.ciclos
        });
        this.persistirCacheLocal(restaurado, false);
      }
    } else if (this.colaSync.tienePendientes()) {
      const conPendientes = this.aplicarDeltaPendienteSobreEstado(actual);
      this.persistirCacheLocal(conPendientes, false);
    }

    // 2. Sincronizar con el servidor si hay conexión
    await this.sincronizarDesdeBackend(true);
  }

  private async registrarEvidenciaConfirmadaEnEstado(info: {
    sesionId: string;
    evidenciaUrl: string;
    evidenciaNombre: string;
    tamanoBytes: number;
    motivo?: string;
  }): Promise<void> {
    const copia: EstadoPersistidoAsistencia = structuredClone(this.estado$.value);
    const idx = copia.sesiones.findIndex(s => s.id === info.sesionId);
    if (idx < 0) return;

    const previa = copia.sesiones[idx];
    const ahora = new Date();
    const iso = ahora.toISOString();
    const actualizada: SesionEntity = {
      ...previa,
      evidenciaBlobId: info.evidenciaUrl,
      evidenciaUrl: info.evidenciaUrl,
      evidenciaNombre: info.evidenciaNombre,
      evidenciaSubidaEn: iso
    };
    copia.sesiones[idx] = actualizada;

    const actor = this.sesionSrv.actor();
    const grupo = copia.grupos.find(g => g.id === previa.grupoId);
    const modulo = copia.modulos.find(m => m.id === previa.moduloId);
    const etiqueta = `${grupo?.codigo ?? previa.grupoId} · Clase ${previa.numero} · ${previa.titulo || modulo?.nombre || `Clase ${previa.numero}`} · ${previa.fecha}`;
    const kbSize = Math.max(1, Math.round((info.tamanoBytes || 1024) / 1024));
    const ultimoEv = copia.auditoria[copia.auditoria.length - 1];
    const sellado = sellarEvento(
      {
        accion: 'EVIDENCIA_CARGADA',
        entidad: 'SESION',
        entidadId: previa.id,
        resumen: `Foto de evidencia con el grupo «${info.evidenciaNombre}» (${kbSize} KB) subida y guardada en ${etiqueta}`,
        cambios: [
          { campo: 'evidenciaNombre', antes: previa.evidenciaNombre ?? null, despues: info.evidenciaNombre },
          { campo: 'evidenciaUrl', antes: previa.evidenciaUrl ?? previa.evidenciaBlobId ?? null, despues: info.evidenciaUrl }
        ],
        motivo: info.motivo || 'Foto con el grupo cargada en la clase',
        contexto: { cicloId: previa.cicloId, sesionId: previa.id, grupoId: previa.grupoId },
        participantesRelacionados: []
      },
      actor,
      ahora,
      ultimoEv?.hash ?? HASH_GENESIS,
      generarId('evt')
    );
    const secuencia = (ultimoEv?.secuencia ?? copia.auditoria.length) + 1;
    const eventoFinal: EventoAuditoria = { ...sellado, secuencia };
    copia.auditoria.push(eventoFinal);

    this.persistirCacheLocal(copia, true);
    this.colaSync.encolarDelta({ sesiones: [actualizada] }, [eventoFinal]);
  }

  private huellaEstado(e: EstadoPersistidoAsistencia): string {
    try {
      return JSON.stringify({
        c: e.ciclos,
        r: e.roles,
        m: e.modulos,
        g: e.grupos,
        s: e.sesiones,
        u: e.usuarios,
        p: e.participantes,
        h: e.habilitaciones,
        i: e.inscripciones,
        a: e.asistencias,
        sol: e.solicitudes,
        seg: e.seguimientos,
        al: e.alertas.length,
        aud: e.auditoria.length
      });
    } catch {
      return String(Date.now());
    }
  }

  private cargarDesdeCacheLocal(): EstadoPersistidoAsistencia {
    if (typeof localStorage === 'undefined') {
      return crearEstadoInicialLimpio();
    }
    try {
      localStorage.removeItem(STORAGE_KEY_LEGACY_V1);
      localStorage.removeItem(STORAGE_KEY_LEGACY_V2);
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const inicial = crearEstadoInicialLimpio();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(inicial));
        return inicial;
      }
      const parsed = JSON.parse(raw) as Partial<EstadoPersistidoAsistencia>;
      const base = crearEstadoInicialLimpio();
      const cargado: EstadoPersistidoAsistencia = {
        ciclos: parsed.ciclos?.length ? parsed.ciclos.map(c => normalizarCiclo(c)) : base.ciclos,
        roles: parsed.roles?.length ? parsed.roles : base.roles,
        modulos: (parsed.modulos ?? []).filter(m => !m.eliminado),
        grupos: (parsed.grupos ?? []).filter(g => !g.eliminado),
        sesiones: (parsed.sesiones ?? []).filter(s => !s.eliminado),
        usuarios: (parsed.usuarios ?? []).filter(u => !u.eliminado),
        participantes: parsed.participantes ?? [],
        habilitaciones: (parsed.habilitaciones ?? []).filter(h => !h.eliminado),
        inscripciones: parsed.inscripciones ?? [],
        asistencias: parsed.asistencias ?? [],
        solicitudes: parsed.solicitudes ?? [],
        seguimientos: parsed.seguimientos ?? [],
        blobs: parsed.blobs ?? [],
        alertas: deduplicarAlertas(parsed.alertas ?? []),
        correosSalida: parsed.correosSalida ?? [],
        preferencias: parsed.preferencias ?? [],
        plantillas: parsed.plantillas ?? [],
        kv: parsed.kv ?? [],
        auditoria: parsed.auditoria ?? []
      };
      return cargado;
    } catch {
      return crearEstadoInicialLimpio();
    }
  }

  private persistirCacheLocal(estadoRaw: EstadoPersistidoAsistencia, emitirBroadcast = true): void {
    const estado: EstadoPersistidoAsistencia = {
      ...estadoRaw,
      alertas: deduplicarAlertas(estadoRaw.alertas)
    };
    this.ultimaHuellaEstado = this.huellaEstado(estado);
    const sinBlobsPesados: EstadoPersistidoAsistencia = {
      ...estado,
      blobs: [],
      alertas: estado.alertas.slice(0, 300),
      correosSalida: estado.correosSalida.slice(0, 150),
      auditoria: estado.auditoria.slice(-500)
    };
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sinBlobsPesados));
      } catch {
        // Ignorar quota exceeded en localStorage; IndexedDB guarda el snapshot completo
      }
    }
    void this.colaSync.guardarSnapshotIndexedDb(sinBlobsPesados);
    this.estado$.next(estado);
    if (emitirBroadcast) {
      this.canalBroadcast?.postMessage({ ts: Date.now() });
    }
  }

  /**
   * Superpone cualquier cambio pendiente en la cola Outbox sobre el estado leído del servidor,
   * garantizando que las lecturas periódicas nunca borren asistencias o cambios pendientes por subir.
   */
  private aplicarDeltaPendienteSobreEstado(base: EstadoPersistidoAsistencia): EstadoPersistidoAsistencia {
    const delta = this.colaSync.obtenerDeltaPendiente();
    const eventosPendientes = this.colaSync.obtenerEventosPendientes();
    if (!delta && !eventosPendientes.length) return base;

    const resultado: EstadoPersistidoAsistencia = structuredClone(base);
    const claveDe = (tabla: string, item: unknown): string => {
      const obj = (item ?? {}) as Record<string, unknown>;
      if (tabla === 'preferencias') return `${obj['usuarioId']}|${obj['tipo']}`;
      if (tabla === 'plantillas') return `${obj['id']}|${obj['version']}`;
      if (tabla === 'kv') return String(obj['clave']);
      if (tabla === 'alertas') return String(obj['dedupKey'] || obj['id']);
      if (tabla === 'correosSalida') return String(obj['idempotencyKey'] || obj['id']);
      return String(obj['id']);
    };

    if (delta) {
      for (const [keyBackend, filasPendientes] of Object.entries(delta)) {
        if (!Array.isArray(filasPendientes) || !filasPendientes.length) continue;
        const tabla = (keyBackend === 'correos' ? 'correosSalida' : keyBackend) as keyof EstadoPersistidoAsistencia;
        const actual = (resultado[tabla] as unknown[]) ?? [];
        const mapa = new Map<string, unknown>();
        for (const item of actual) {
          mapa.set(claveDe(tabla, item), item);
        }
        for (const fila of filasPendientes) {
          const clave = claveDe(tabla, fila);
          const objFila = fila as { eliminado?: boolean };
          if ((tabla === 'usuarios' || tabla === 'grupos' || tabla === 'sesiones' || tabla === 'modulos' || tabla === 'habilitaciones') && objFila.eliminado) {
            mapa.delete(clave);
          } else {
            mapa.set(clave, fila);
          }
        }
        (resultado as unknown as Record<string, unknown[]>)[tabla] = Array.from(mapa.values());
      }
    }

    if (eventosPendientes.length) {
      const yaIds = new Set(resultado.auditoria.map(e => e.id));
      for (const ev of eventosPendientes) {
        if (!yaIds.has(ev.id)) {
          resultado.auditoria.push(ev);
        }
      }
    }

    return resultado;
  }

  private async sincronizarDesdeBackend(forzarEmision = false): Promise<void> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }
    if (this.lecturaEnCursoPromise) {
      if (forzarEmision) {
        this.releerPendienteForzado = true;
      }
      return this.lecturaEnCursoPromise;
    }

    const ahoraMs = Date.now();
    const ultimaGlobalMs =
      typeof localStorage !== 'undefined'
        ? Number(localStorage.getItem('mj_asistencia_mml_last_fetch_ms') || 0)
        : 0;
    const ultimaEfectivaMs = Math.max(this.ultimaLecturaExitosaMs, ultimaGlobalMs);
    if (
      !forzarEmision &&
      (this.escriturasEnCurso > 0 ||
        ahoraMs - this.ultimaEscrituraMs < 2500 ||
        ahoraMs - ultimaEfectivaMs < 8000)
    ) {
      return;
    }

    this.lecturaEnCursoPromise = this.ejecutarLecturaDesdeBackend(forzarEmision).finally(() => {
      this.lecturaEnCursoPromise = null;
      if (this.releerPendienteForzado) {
        this.releerPendienteForzado = false;
        void this.sincronizarDesdeBackend(true);
      }
    });

    return this.lecturaEnCursoPromise;
  }

  private async ejecutarLecturaDesdeBackend(forzarEmision: boolean): Promise<void> {
    await this.colaSync.esperarHidratacionInicial();

    const versionAlIniciar = this.versionEscritura;
    const inicioLecturaMs = Date.now();
    this.ultimaLecturaExitosaMs = inicioLecturaMs;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('mj_asistencia_mml_last_fetch_ms', String(inicioLecturaMs));
      } catch {
        // Ignorar errores de almacenamiento
      }
    }
    this.canalBroadcast?.postMessage({ ts: inicioLecturaMs, soloLatido: true });
    try {
      const opcionesGet = this.colaSync.tienePendientes()
        ? { params: { _sync: String(inicioLecturaMs) } }
        : undefined;
      const resp = await firstValueFrom(
        this.http.get<{
          tablasCreadas: boolean;
          datos: Partial<EstadoPersistidoAsistencia> & { correos?: CorreoSalidaEntity[] } | null;
        }>(`${this.apiUrl}mml-asistencia/estado`, opcionesGet)
      );
      this.ultimaLecturaExitosaMs = Date.now();
      if (!forzarEmision && (this.versionEscritura !== versionAlIniciar || this.escriturasEnCurso > 0 || this.ultimaEscrituraMs >= inicioLecturaMs)) {
        return;
      }
      if (resp?.tablasCreadas && resp.datos) {
        const actual = this.estado$.value;
        const fusionadoServidor: EstadoPersistidoAsistencia = {
          ciclos: resp.datos.ciclos?.length
            ? resp.datos.ciclos.map(c => normalizarCiclo(c))
            : actual.ciclos.map(c => normalizarCiclo(c)),
          roles: resp.datos.roles?.length ? resp.datos.roles : actual.roles,
          modulos: Array.isArray(resp.datos.modulos)
            ? resp.datos.modulos.filter(m => !m.eliminado)
            : actual.modulos,
          grupos: Array.isArray(resp.datos.grupos)
            ? resp.datos.grupos.filter(g => !g.eliminado)
            : actual.grupos,
          sesiones: Array.isArray(resp.datos.sesiones)
            ? resp.datos.sesiones.filter(s => !s.eliminado)
            : actual.sesiones,
          usuarios: Array.isArray(resp.datos.usuarios)
            ? resp.datos.usuarios.filter(u => !u.eliminado)
            : actual.usuarios,
          participantes: Array.isArray(resp.datos.participantes)
            ? resp.datos.participantes
            : actual.participantes,
          habilitaciones: Array.isArray(resp.datos.habilitaciones)
            ? resp.datos.habilitaciones.filter(h => !h.eliminado)
            : actual.habilitaciones,
          inscripciones: Array.isArray(resp.datos.inscripciones)
            ? resp.datos.inscripciones
            : actual.inscripciones,
          asistencias: Array.isArray(resp.datos.asistencias)
            ? resp.datos.asistencias
            : actual.asistencias,
          solicitudes: Array.isArray(resp.datos.solicitudes)
            ? resp.datos.solicitudes
            : actual.solicitudes,
          seguimientos: Array.isArray(resp.datos.seguimientos)
            ? resp.datos.seguimientos
            : actual.seguimientos,
          blobs: actual.blobs,
          alertas: Array.isArray(resp.datos.alertas)
            ? resp.datos.alertas
            : actual.alertas,
          correosSalida: Array.isArray(resp.datos.correos)
            ? resp.datos.correos
            : actual.correosSalida,
          preferencias: Array.isArray(resp.datos.preferencias)
            ? resp.datos.preferencias
            : actual.preferencias,
          plantillas: Array.isArray(resp.datos.plantillas)
            ? resp.datos.plantillas
            : actual.plantillas,
          kv: Array.isArray(resp.datos.kv)
            ? resp.datos.kv
            : actual.kv,
          auditoria: Array.isArray(resp.datos.auditoria)
            ? resp.datos.auditoria
            : actual.auditoria
        };

        // Si este dispositivo tenía cola pendiente (ej. tablet offline), reconciliar fila por fila contra
        // el servidor antes de superponer o enviar: descarta lo que ya esté más reciente en BD (subido desde PC)
        // y conserva únicamente los registros que realmente sean más nuevos aquí.
        if (this.colaSync.tienePendientes()) {
          const rec = await this.colaSync.reconciliarContraEstadoServidor(fusionadoServidor);
          if (rec.huboDescartesPorServidorMasReciente) {
            forzarEmision = true;
          }
        }

        // Superponer únicamente los cambios locales que sigan genuinamente vigentes en la cola
        const protegido = this.aplicarDeltaPendienteSobreEstado(fusionadoServidor);
        const nuevaHuella = this.huellaEstado(protegido);
        const cambioDatos = nuevaHuella !== this.ultimaHuellaEstado;
        if (forzarEmision || cambioDatos) {
          this.persistirCacheLocal(protegido, cambioDatos);
        }
        if (!cambioDatos) {
          this.canalBroadcast?.postMessage({ ts: Date.now(), soloLatido: true });
        }

        // Si tras reconciliar todavía quedan registros más recientes por subir (sin backoff activo), enviarlos ahora
        if (this.colaSync.tienePendientes() && !this.colaSync.tieneReintentoProgramado()) {
          void this.colaSync.flush();
        }
      }
    } catch {
      // Si el backend no responde o estamos offline, se mantiene el estado local intacto
    }
  }

  private enviarCambiosAlBackend(
    delta: Record<string, unknown>,
    nuevosEventosAuditoria: EventoAuditoria[]
  ): void {
    this.versionEscritura++;
    this.ultimaEscrituraMs = Date.now();
    this.colaSync.encolarDelta(delta, nuevosEventosAuditoria);
  }

  private armarDatosCiclo(estado: EstadoPersistidoAsistencia, cicloId: string): DatosCiclo {
    const ciclo = estado.ciclos.find(c => c.id === cicloId) ?? estado.ciclos[0];
    if (!ciclo) {
      throw new Error(`Ciclo no encontrado: ${cicloId}`);
    }
    const targetId = ciclo.id;
    return {
      ciclo,
      roles: estado.roles.filter(r => r.cicloId === targetId),
      modulos: estado.modulos.filter(m => m.cicloId === targetId && !m.eliminado),
      grupos: estado.grupos.filter(g => g.cicloId === targetId && !g.eliminado),
      sesiones: estado.sesiones.filter(s => s.cicloId === targetId && !s.eliminado),
      usuarios: estado.usuarios.filter(u => !u.eliminado),
      participantes: estado.participantes,
      habilitaciones: estado.habilitaciones.filter(h => h.cicloId === targetId && !h.eliminado),
      inscripciones: estado.inscripciones.filter(i => i.cicloId === targetId),
      asistencias: estado.asistencias.filter(a => a.cicloId === targetId),
      solicitudes: estado.solicitudes.filter(s => s.cicloId === targetId),
      seguimientos: estado.seguimientos.filter(s => s.cicloId === targetId)
    };
  }

  async transaccion<T>(fn: (uow: UnidadTrabajo) => Promise<T>): Promise<T> {
    await this.cargaInicialPromise;
    const copia: EstadoPersistidoAsistencia = structuredClone(this.estado$.value);
    const delta: Record<string, unknown[]> = {};
    const nuevosEventos: EventoAuditoria[] = [];

    const claveDe = (tabla: TablaAsistencia, item: unknown): string => {
      const obj = item as Record<string, unknown>;
      if (tabla === 'preferencias') return `${obj['usuarioId']}|${obj['tipo']}`;
      if (tabla === 'plantillas') return `${obj['id']}|${obj['version']}`;
      if (tabla === 'kv') return String(obj['clave']);
      if (tabla === 'alertas') return String(obj['dedupKey'] || obj['id']);
      if (tabla === 'correosSalida') return String(obj['idempotencyKey'] || obj['id']);
      return String(obj['id']);
    };

    const uow: UnidadTrabajo = {
      datosCiclo: async (cicloId: string) => this.armarDatosCiclo(copia, cicloId),
      obtener: async <K extends TablaConId>(tabla: K, id: string) => {
        const lista = copia[tabla] as unknown as Array<{ id: string }>;
        return lista.find(x => x.id === id) as EntidadesPorTabla[K] | undefined;
      },
      guardar: async <K extends TablaAsistencia>(tabla: K, filas: EntidadesPorTabla[K][]) => {
        if (!filas.length) return;
        const actual = [...(copia[tabla] as unknown[])];
        const mapa = new Map<string, unknown>();
        for (const item of actual) {
          mapa.set(claveDe(tabla, item), item);
        }
        for (const fila of filas) {
          const clave = claveDe(tabla, fila);
          const objFila = fila as { eliminado?: boolean };
          if ((tabla === 'usuarios' || tabla === 'grupos' || tabla === 'sesiones' || tabla === 'modulos' || tabla === 'habilitaciones') && objFila.eliminado) {
            mapa.delete(clave);
          } else {
            mapa.set(clave, fila);
          }
        }
        (copia as unknown as Record<string, unknown[]>)[tabla] = Array.from(mapa.values());
        const keyBackend = tabla === 'correosSalida' ? 'correos' : tabla;
        delta[keyBackend] = [...(delta[keyBackend] ?? []), ...filas];
      },
      todos: async <K extends TablaAsistencia>(tabla: K) => {
        return [...(copia[tabla] as unknown as EntidadesPorTabla[K][])];
      },
      ultimoEvento: async () => {
        if (!copia.auditoria.length) return undefined;
        return copia.auditoria[copia.auditoria.length - 1];
      },
      agregarEvento: async (evento: EventoAuditoria) => {
        const ultimo = copia.auditoria[copia.auditoria.length - 1];
        const secuencia = (ultimo?.secuencia ?? copia.auditoria.length) + 1;
        const conSecuencia: EventoAuditoria = { ...evento, secuencia };
        copia.auditoria.push(conSecuencia);
        nuevosEventos.push(conSecuencia);
        return secuencia;
      },
      eventosDesde: async (iso: string) => {
        return copia.auditoria.filter(e => e.timestamp >= iso);
      },
      dedupExistentes: async (claves: string[]) => {
        if (!claves.length) return new Set<string>();
        const set = new Set(claves);
        return new Set(copia.alertas.filter(a => set.has(a.dedupKey)).map(a => a.dedupKey));
      },
      alertasAbiertas: async () => {
        return copia.alertas.filter(a => a.estado === 'NUEVA' || a.estado === 'LEIDA');
      },
      correosCreadosDesde: async (iso: string) => {
        return copia.correosSalida.filter(c => c.creadoEn >= iso);
      },
      kvObtener: async <V>(clave: string) => {
        return copia.kv.find(k => k.clave === clave)?.valor as V | undefined;
      },
      kvGuardar: async (clave: string, valor: unknown) => {
        const idx = copia.kv.findIndex(k => k.clave === clave);
        const item: KvEntity = { clave, valor };
        if (idx >= 0) copia.kv[idx] = item;
        else copia.kv.push(item);
        delta['kv'] = [...(delta['kv'] ?? []), item];
      }
    };

    const resultado = await fn(uow);
    const clavesDelta = Object.keys(delta).filter(k => {
      if (k === 'kv') {
        return (delta['kv'] as KvEntity[]).some(item => item.clave !== 'lockProgramador');
      }
      return (delta[k]?.length ?? 0) > 0;
    });
    if (clavesDelta.length > 0 || nuevosEventos.length > 0) {
      this.persistirCacheLocal(copia);
      this.enviarCambiosAlBackend(delta, nuevosEventos);
    } else if (delta['kv']?.length) {
      this.estado$.value.kv = copia.kv;
    }
    return resultado;
  }

  observarCiclos(): Observable<CicloEntity[]> {
    return this.estado$.pipe(map(s => s.ciclos));
  }

  observarDatosCiclo(cicloId: string): Observable<DatosCiclo> {
    return this.estado$.pipe(map(s => this.armarDatosCiclo(s, cicloId)));
  }

  observarAlertasDe(destinatarioId: string): Observable<AlertaEntity[]> {
    return this.estado$.pipe(
      map(s =>
        s.alertas
          .filter(a => a.destinatarioId === destinatarioId && a.motivoCierre !== 'UNIFICADA_AUTO')
          .slice()
          .sort((a, b) => b.creadaEn.localeCompare(a.creadaEn))
      )
    );
  }

  observarTodasAlertas(): Observable<AlertaEntity[]> {
    return this.estado$.pipe(
      map(s => s.alertas.filter(a => a.motivoCierre !== 'UNIFICADA_AUTO').slice().sort((a, b) => b.creadaEn.localeCompare(a.creadaEn)))
    );
  }

  observarCorreos(): Observable<CorreoSalidaEntity[]> {
    return this.estado$.pipe(
      map(s => s.correosSalida.slice().sort((a, b) => b.creadoEn.localeCompare(a.creadoEn)))
    );
  }

  observarAuditoria(): Observable<EventoAuditoria[]> {
    return this.estado$.pipe(map(s => s.auditoria.slice().reverse()));
  }

  observarPreferencias(usuarioId: string): Observable<PreferenciaNotificacionEntity[]> {
    return this.estado$.pipe(
      map(s => s.preferencias.filter(p => p.usuarioId === usuarioId))
    );
  }

  observarPlantillas(): Observable<PlantillaCorreoEntity[]> {
    return this.estado$.pipe(map(s => s.plantillas));
  }

  observarKv<T>(clave: string): Observable<T | undefined> {
    return this.estado$.pipe(
      map(s => s.kv.find(k => k.clave === clave)?.valor as T | undefined)
    );
  }

  async obtenerBlob(id: string): Promise<BlobEntity | undefined> {
    return this.estado$.value.blobs.find(b => b.id === id);
  }

  async todosEventos(): Promise<EventoAuditoria[]> {
    return [...this.estado$.value.auditoria].sort((a, b) => (a.secuencia ?? 0) - (b.secuencia ?? 0));
  }
}
